import { useState } from "react";
import useSWR from "swr";
import { fetcher } from "../../../../../services/fetcher";
import {
  Button,
  Flex,
  Modal,
  Text,
  Paper,
  Pagination,
  ActionIcon,
  Table,
  Menu,
  TextInput,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconChevronDown,
  IconChevronUp,
  IconDots,
  IconEdit,
  IconTrash,
} from "@tabler/icons-react";

import useRbac from "../../../../../common/hooks/useRbac/useRbac";

import AddEditBenefits from "../../forms/AddEditBenefits";
import MainLoader from "../../../../../common/components/Loader/Loader";

import {
  errorNotifications,
  successNotifications,
} from "../../../../../utils/handleNotifications";
import iconSearch from "../../../../../assets/svg/search.svg";
import dataTableClasses from "../../../../../common/components/DataTable/DataTable.module.css";

const endpoint = "cover-api/benefits/";

const Benefits = () => {
  const { checkPermission } = useRbac();
  const [opened, { open, close }] = useDisclosure(false);
  const [editingBenefit, setEditingBenefit] = useState(null);
  const [benefitId, setBenefitId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);
  const [benefitToDelete, setBenefitToDelete] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [activePage, setActivePage] = useState(1);
  const [sortDirection, setSortDirection] = useState("asc");

  const pageSize = 12;

  const { data, isLoading, error, mutate } = useSWR(endpoint, fetcher);

  const { data: benefitData } = useSWR(
    benefitId ? `${endpoint}${benefitId}/` : null,
    fetcher
  );

  const addBenefitMutation = async (benefit) => {
    setIsSubmitting(true);
    try {
      await fetcher(endpoint, "POST", benefit);
      await mutate();
      successNotifications("Success! Benefit added");
      close();
    } catch (error) {
      console.log(error);
      errorNotifications(error.response.data, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateBenefitMutation = async (benefit) => {
    setIsSubmitting(true);
    try {
      await fetcher(`${endpoint}${benefit.id}/`, "PATCH", benefit);
      await mutate();
      successNotifications("Success! Benefit edited");
      close();
    } catch (error) {
      console.log(error);
      errorNotifications(error.response.data, error);
    } finally {
      setIsSubmitting(false);
      setBenefitId(null);
      setEditingBenefit(null);
    }
  };

  const deleteBenefitMutation = async (benefitId) => {
    setIsSubmitting(true);
    try {
      await fetcher(`${endpoint}${benefitId}/`, "DELETE");
      await mutate();
      successNotifications("Success! Benefit deleted");
      closeDeleteModal();
      setBenefitToDelete(null);
    } catch (error) {
      console.log(error);
      errorNotifications(error.response.data, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = (data) => {
    if (editingBenefit) {
      updateBenefitMutation({ ...editingBenefit, ...data });
    } else {
      addBenefitMutation(data);
    }
  };

  const handleEdit = (benefit) => {
    if (!checkPermission("update")) return;
    setEditingBenefit(benefit);
    setBenefitId(benefit.id);
    open();
  };

  const handleDelete = (benefitId) => {
    if (!checkPermission("delete")) return;
    setBenefitToDelete(benefitId);
    openDeleteModal();
  };

  const confirmDelete = () => {
    if (benefitToDelete) {
      deleteBenefitMutation(benefitToDelete);
    }
  };

  const normalizeProvider = (value) => {
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "string") return value;
    if (value == null) return "";
    return String(value);
  };

  const normalizedSearch = searchValue.trim().toLowerCase();
  const filteredData = (data || []).filter((benefit) => {
    if (!normalizedSearch) return true;

    return (
      benefit.code?.toLowerCase().includes(normalizedSearch) ||
      benefit.name?.toLowerCase().includes(normalizedSearch) ||
      benefit.description?.toLowerCase().includes(normalizedSearch) ||
      normalizeProvider(benefit.provider_names)
        .toLowerCase()
        .includes(normalizedSearch)
    );
  });

  const sortedData = [...filteredData].sort((a, b) => {
    const providerA = normalizeProvider(a.provider_names).toLowerCase();
    const providerB = normalizeProvider(b.provider_names).toLowerCase();
    if (providerA === providerB) return 0;

    const order = providerA > providerB ? 1 : -1;
    return sortDirection === "asc" ? order : -order;
  });

  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));
  const currentPage = Math.min(activePage, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  if (isLoading) {
    return <MainLoader />;
  }

  if (error) {
    return <p>Something went wrong</p>;
  }

  if (!checkPermission("view")) {
    return <div>Not authorized to view this module</div>;
  }

  return (
    <>
      <Flex
        justify="space-between"
        align="center"
        gap="md"
        wrap="wrap"
        mb="lg"
      >
        <Flex align="center" gap="sm" style={{ flex: 1, minWidth: 320 }}>
          <TextInput
            value={searchValue}
            onChange={(event) => {
              setSearchValue(event.currentTarget.value);
              setActivePage(1);
            }}
            placeholder="Search here..."
            variant="default"
            styles={{
              input: {
                backgroundColor: "#fff",
                padding: "8px 34px",
                borderRadius: "8px",
                border: "1px solid rgba(108, 148, 180, 0.15)",
              },
            }}
            leftSection={
              <img src={iconSearch} alt="" style={{ width: 16, height: 16 }} />
            }
            style={{ width: 240, maxWidth: "100%" }}
          />
        </Flex>

        {checkPermission("create") && (
          <Button
            variant="gradient"
            size="lg"
            fz={14}
            fw={600}
            onClick={() => {
              setBenefitId(null);
              setEditingBenefit(null);
              open();
            }}
          >
            + Add Plan Benefits
          </Button>
        )}
      </Flex>

      <Paper
        style={{
          overflow: "hidden",
          // border: "1px solid #dce8f2",
          borderRadius: 12,
          background: "#ffffff",
        }}
      >
        <div className={dataTableClasses.tableWrapper}>
          <Table
            horizontalSpacing="xl"
            verticalSpacing="md"
            style={{ minWidth: 1080 }}
          >
            <Table.Thead
              className={dataTableClasses.theadSticky}
              style={{ backgroundColor: "#ffffff" }}
            >
              <Table.Tr>
                <Table.Th>Benefit Code</Table.Th>
                <Table.Th>Benefit Name</Table.Th>
          
                <Table.Th
                  style={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => {
                    setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
                    setActivePage(1);
                  }}
                >
                  <Flex align="center" gap={4}>
                    <span>Issuance Company</span>
                    {sortDirection === "asc" ? (
                      <IconChevronUp size={14} />
                    ) : (
                      <IconChevronDown size={14} />
                    )}
                  </Flex>
                </Table.Th>
                <Table.Th style={{ width: 90, textAlign: "center" }}>
                  Action
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {paginatedData.length === 0 && (
                <Table.Tr>
                  <Table.Td
                    colSpan={4}
                    style={{
                      textAlign: "center",
                      color: "#6c94b4",
                      fontSize: 15,
                      padding: "20px 0",
                    }}
                  >
                    No benefits found
                  </Table.Td>
                </Table.Tr>
              )}
              {paginatedData.map((benefit, index) => (
                <Table.Tr
                  key={benefit.id}
                  style={{
                    backgroundColor: index % 2 === 0 ? "#F8F8F8" : "#FFF",
                  }}
                >
                  <Table.Td style={{ color: "#16385b", fontWeight: 600 }}>
                    {benefit.code}
                  </Table.Td>
                  <Table.Td
                    style={{
                      maxWidth: 280,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      color: "#5f86a7",
                      fontWeight: 500,
                    }}
                  >
                    {benefit.name}
                  </Table.Td>
                  
                  <Table.Td
                    style={{
                      color: "#6c94b4",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {normalizeProvider(benefit.provider_names) || "-"}
                  </Table.Td>
                  <Table.Td style={{ width: 90, textAlign: "center" }}>
                    {(checkPermission("update") || checkPermission("delete")) && (
                      <Menu position="bottom-end" shadow="md" withinPortal>
                        <Menu.Target>
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            aria-label={`Actions for ${benefit.code}`}
                          >
                            <IconDots size={18} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          {checkPermission("update") && (
                            <Menu.Item
                              leftSection={<IconEdit size={14} />}
                              onClick={() => handleEdit(benefit)}
                            >
                              Edit
                            </Menu.Item>
                          )}
                          {checkPermission("delete") && (
                            <Menu.Item
                              color="red"
                              leftSection={<IconTrash size={14} />}
                              onClick={() => handleDelete(benefit.id)}
                            >
                              Delete
                            </Menu.Item>
                          )}
                        </Menu.Dropdown>
                      </Menu>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </div>
      </Paper>

      <Flex justify="space-between" align="center" mt="lg" wrap="wrap" gap="md">
        <Text style={{ color: "#6c94b4", fontSize: 14, fontWeight: 500 }}>
          Showing {sortedData.length === 0 ? 0 : startIndex + 1} to{" "}
          {Math.min(endIndex, sortedData.length)} of {sortedData.length} entries
        </Text>

        <Pagination
          value={currentPage}
          onChange={setActivePage}
          total={totalPages}
          siblings={1}
          withEdges
          styles={{
            control: {
              minWidth: 36,
              height: 36,
              borderColor: "#c8d9e8",
              color: "#5f86a7",
            },
            active: {
              background: "#5f86a7",
              borderColor: "#5f86a7",
              color: "#ffffff",
            },
          }}
        />
      </Flex>

      <Modal
        opened={opened}
        onClose={close}
        size="50rem"
        centered
        title={editingBenefit ? "Edit Benefit" : "Add Benefit"}
        closeButtonProps={{
          icon: (
            <img
              src="/images/svg/close.svg"
              style={{ width: "1.6rem" }}
              alt="close-icon"
            />
          ),
        }}
      >
        <AddEditBenefits
          onSubmit={onSubmit}
          closeModal={close}
          initialData={benefitData || null}
          isSubmitting={isSubmitting}
        />
      </Modal>

      <Modal
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        title={
          <span
            style={{
              color: "#333",
              fontFamily: "Montserrat",
              fontSize: "14px",
              fontWeight: "600",
              lineHeight: "normal",
            }}
          >
            Confirm Deletion
          </span>
        }
        centered
      >
        <Text
          style={{
            fontFamily: "Montserrat, sans-serif",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          Are you sure you want to delete this benefit?
        </Text>
        <Flex justify="center" mt="lg">
          <Button variant="outline" color="gray" onClick={closeDeleteModal}>
            Cancel
          </Button>
          <Button color="red" ml="md" onClick={confirmDelete}>
            Delete
          </Button>
        </Flex>
      </Modal>
    </>
  );
};

export default Benefits;